'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import Image from 'next/image';

interface ProductGalleryProps {
  images: string[];
  /** Erişilebilirlik ve SEO için ana görsel alt metni */
  productName?: string;
}

export function ProductGallery({ images, productName }: ProductGalleryProps) {
  const [activeImage, setActiveImage] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [lastOffset, setLastOffset] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const imgContainerRef = useRef<HTMLDivElement>(null);

  // ESC ile modal kapama + ok tuşlarıyla gezinme
  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
      if (e.key === 'ArrowRight') setActiveImage((prev) => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setActiveImage((prev) => (prev - 1 + images.length) % images.length);
      // +/- ile zoom
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, z + 0.3));
      if (e.key === '-') setZoom((z) => Math.max(1, z - 0.3));
      if (e.key === '0') {
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setLastOffset({ x: 0, y: 0 });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, images.length]);

  // Native wheel event listener - passive: false ile scroll'u engelleme
  useEffect(() => {
    const container = imgContainerRef.current;
    if (!container || !isModalOpen) return;

    const handleNativeWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = -e.deltaY * 0.002;
      setZoom((prevZoom) => {
        const newZoom = Math.max(1, Math.min(prevZoom + delta, 4));
        if (newZoom === 1) {
          setOffset({ x: 0, y: 0 });
          setLastOffset({ x: 0, y: 0 });
        }
        return newZoom;
      });
    };

    container.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleNativeWheel);
  }, [isModalOpen]);

  // Modal açıkken body scroll'unu kilitle ve navigasyon barını gizle
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('gallery-modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('gallery-modal-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('gallery-modal-open');
    };
  }, [isModalOpen]);

  // Modal dışında tıklayınca kapama (backdrop veya içerik alanı boşluğuna tıklanınca)
  const handleModalClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Sadece backdrop veya içerik wrapper'ına tıklanınca kapat, butonlara/görsele değil
    if (
      target === modalRef.current ||
      target.dataset.backdrop === 'true'
    ) {
      setModalOpen(false);
    }
  }, []);

  // Mouse ile sürükleme (pan)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom === 1) return;
    e.preventDefault();
    setDragging(true);
    setStart({ x: e.clientX, y: e.clientY });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setOffset({
      x: lastOffset.x + (e.clientX - start.x),
      y: lastOffset.y + (e.clientY - start.y),
    });
  };
  const handleMouseUp = () => {
    if (!dragging) return;
    setDragging(false);
    setLastOffset(offset);
  };

  // Çift tıklama ile zoom toggle
  const handleDoubleClick = useCallback(() => {
    if (zoom > 1) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setLastOffset({ x: 0, y: 0 });
    } else {
      setZoom(2.5);
    }
  }, [zoom]);

  // Touch desteği
  const touchStartRef = useRef<{ dist: number; zoom: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchStartRef.current = { dist: Math.hypot(dx, dy), zoom };
    } else if (e.touches.length === 1 && zoom > 1) {
      setDragging(true);
      setStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStartRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const scale = dist / touchStartRef.current.dist;
      const newZoom = Math.max(1, Math.min(touchStartRef.current.zoom * scale, 4));
      setZoom(newZoom);
      if (newZoom === 1) {
        setOffset({ x: 0, y: 0 });
        setLastOffset({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && dragging) {
      setOffset({
        x: lastOffset.x + (e.touches[0].clientX - start.x),
        y: lastOffset.y + (e.touches[0].clientY - start.y),
      });
    }
  }, [dragging, lastOffset, start]);

  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null;
    if (dragging) {
      setDragging(false);
      setLastOffset(offset);
    }
  }, [dragging, offset]);

  // Modal açıldığında zoom ve pan sıfırlansın
  useEffect(() => {
    if (isModalOpen) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setLastOffset({ x: 0, y: 0 });
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isModalOpen, activeImage]);

  // Zoom sıfırlama
  const resetZoom = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setLastOffset({ x: 0, y: 0 });
  }, []);

  if (!images || images.length === 0) {
    return (
      <div className="relative w-full aspect-[4/3] min-h-[16rem] max-h-[24rem] bg-gray-100 rounded-lg flex items-center justify-center">
        <span className="text-gray-500">Görsel bulunamadı</span>
      </div>
    );
  }

  const mainAltBase = productName?.trim() || 'Filiz Kozmetik ürünü';
  const mainAlt =
    images.length > 1 ? `${mainAltBase} — görsel ${activeImage + 1}` : mainAltBase;

  return (
    <div className="space-y-4">
      {/* Ana görsel — sade, çerçevesiz */}
      <div
        className="relative w-full aspect-[4/3] min-h-[16rem] max-h-[min(24rem,70vh)] rounded-lg overflow-hidden flex items-center justify-center cursor-zoom-in group"
        onClick={() => setModalOpen(true)}
      >
        <Image
          src={images[activeImage]}
          alt={mainAlt}
          fill
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-contain z-10 transition-transform duration-500 group-hover:scale-105"
          priority
          fetchPriority="high"
          quality={85}
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 flex items-center justify-center z-20">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-75">
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((image, index) => (
            <div
              key={index}
              className={`relative aspect-square w-full min-h-[5rem] bg-white rounded-lg overflow-hidden border-2 cursor-pointer transition-all duration-300 ${
                index === activeImage 
                  ? 'border-blue-500 shadow-lg ring-2 ring-blue-500/20' 
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              onClick={() => setActiveImage(index)}
            >
              <Image
                src={image}
                alt={`${mainAltBase} — önizleme ${index + 1}`}
                fill
                sizes="(max-width: 768px) 22vw, 120px"
                className={`object-cover transition-all duration-300 ${
                  index === activeImage ? 'scale-100' : 'hover:scale-110'
                }`}
                loading="lazy"
                quality={75}
              />
              {index !== activeImage && (
                <div className="absolute inset-0 bg-black/5 hover:bg-transparent transition-colors duration-300" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal - Premium Tasarım */}
      {isModalOpen && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={handleModalClick}
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(15,15,25,0.95) 100%)',
            backdropFilter: 'blur(12px)',
            animation: 'galleryModalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Üst bar - Başlık ve kontroller */}
          <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4"
               style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' }}>
            <div className="flex items-center gap-3">
              {images.length > 1 && (
                <span className="text-white/70 text-sm font-medium tracking-wide">
                  {activeImage + 1} / {images.length}
                </span>
              )}
              {productName && (
                <span className="hidden sm:inline text-white/50 text-sm truncate max-w-[200px]">
                  {productName}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Sıfırla butonu */}
              {zoom > 1 && (
                <button
                  onClick={resetZoom}
                  className="text-white/70 hover:text-white rounded-lg p-2 hover:bg-white/10 transition-all duration-200"
                  title="Sıfırla (0)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
              {/* Kapat butonu */}
              <button
                onClick={() => setModalOpen(false)}
                className="text-white/70 hover:text-white rounded-lg p-2 hover:bg-white/10 transition-all duration-200"
                title="Kapat (ESC)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Ana içerik alanı - data-backdrop ile boşluk tıklaması yakalanır */}
          <div
            className="relative w-full h-full flex flex-col items-center justify-center px-2 sm:px-4"
            style={{ paddingTop: '70px', paddingBottom: images.length > 1 ? '110px' : '70px' }}
            data-backdrop="true"
            onClick={handleModalClick}
          >

            {/* Sol Ok */}
            {images.length > 1 && (
              <button
                onClick={() => setActiveImage((prev) => (prev - 1 + images.length) % images.length)}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 group/arrow"
                title="Önceki görsel"
              >
                <div className="bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full p-2.5 sm:p-3 transition-all duration-300 group-hover/arrow:scale-110 border border-white/10 hover:border-white/20">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-white/80 group-hover/arrow:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </div>
              </button>
            )}

            {/* Sağ Ok */}
            {images.length > 1 && (
              <button
                onClick={() => setActiveImage((prev) => (prev + 1) % images.length)}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 group/arrow"
                title="Sonraki görsel"
              >
                <div className="bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full p-2.5 sm:p-3 transition-all duration-300 group-hover/arrow:scale-110 border border-white/10 hover:border-white/20">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-white/80 group-hover/arrow:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            )}

            {/* Büyük Görsel (Zoom/Pan) */}
            <div
              ref={imgContainerRef}
              className="relative flex items-center justify-center overflow-hidden rounded-lg bg-black/40"
              style={{
                width: '100%',
                maxWidth: '800px',
                height: `calc(100vh - ${images.length > 1 ? '220px' : '160px'})`,
                maxHeight: '65vh',
                cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
                touchAction: 'none',
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onDoubleClick={handleDoubleClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Statik Bulanık Arka Plan (Zoom ile hareket etmez, modal boşluklarını doldurur) */}
              <div className="absolute inset-0 overflow-hidden opacity-25 blur-3xl scale-110 pointer-events-none select-none">
                <Image
                  src={images[activeImage]}
                  alt=""
                  fill
                  className="object-cover"
                  sizes="30vw"
                  priority
                />
                <div className="absolute inset-0 bg-black/35" />
              </div>

              <div
                className="relative w-full h-full z-10"
                style={{
                  transform: `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`,
                  transition: dragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                  transformOrigin: 'center center',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Image
                  src={images[activeImage]}
                  alt={`${mainAltBase} — büyük görüntü ${activeImage + 1}`}
                  fill
                  sizes="(max-width: 1200px) 90vw, 900px"
                  className="object-contain select-none pointer-events-none"
                  draggable={false}
                  loading="lazy"
                  style={{
                    userSelect: 'none',
                    animation: isAnimating ? 'galleryImageIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' : undefined,
                  }}
                  quality={95}
                />
              </div>
            </div>

            {/* Dikey Zoom kontrol çubuğu — sağ kenarda */}
            <div className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20">
              <div className="flex flex-col items-center gap-1 bg-black/40 backdrop-blur-xl rounded-full px-1 py-1.5 border border-white/10">
                <button
                  onClick={() => setZoom((z) => Math.min(4, z + 0.3))}
                  className="text-white/70 hover:text-white rounded-full p-1.5 hover:bg-white/10 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={zoom >= 4}
                  title="Yakınlaştır (+)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>

                {/* Dikey Zoom slider bar */}
                <div className="relative h-20 sm:h-28 w-6 flex items-center justify-center py-1">
                  <div className="absolute inset-y-1 left-1/2 -translate-x-1/2 w-[3px] bg-white/15 rounded-full overflow-hidden">
                    <div
                      className="w-full bg-gradient-to-t from-blue-400 to-blue-500 rounded-full transition-all duration-200"
                      style={{ height: `${((zoom - 1) / 3) * 100}%` }}
                    />
                  </div>
                  <input
                    type="range"
                    min={100}
                    max={400}
                    value={zoom * 100}
                    onChange={(e) => {
                      const newZoom = Number(e.target.value) / 100;
                      setZoom(newZoom);
                      if (newZoom === 1) {
                        setOffset({ x: 0, y: 0 });
                        setLastOffset({ x: 0, y: 0 });
                      }
                    }}
                    className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
                    style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                    title="Zoom seviyesi"
                  />
                </div>

                <button
                  onClick={() => setZoom((z) => Math.max(1, z - 0.3))}
                  className="text-white/70 hover:text-white rounded-full p-1.5 hover:bg-white/10 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={zoom <= 1}
                  title="Uzaklaştır (-)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>

                <span className="text-white/60 text-[10px] font-mono min-w-[28px] text-center tabular-nums select-none">
                  {Math.round(zoom * 100)}%
                </span>
              </div>
            </div>
          </div>

          {/* Alt thumbnail şeridi */}
          {images.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 z-30 px-4 py-3 sm:py-4"
                 style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }}>
              <div className="flex items-center justify-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                      index === activeImage
                        ? 'ring-2 ring-white scale-105 shadow-lg shadow-white/10'
                        : 'ring-1 ring-white/20 opacity-50 hover:opacity-80 hover:ring-white/40'
                    }`}
                  >
                    <Image
                      src={image}
                      alt={`Önizleme ${index + 1}`}
                      fill
                      sizes="64px"
                      className="object-cover"
                      loading="lazy"
                      quality={50}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CSS Animasyonları */}
          <style jsx global>{`
            @keyframes galleryModalIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes galleryImageIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
            .scrollbar-hide {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          `}</style>
        </div>
      )}
    </div>
  );
}